import React, { useEffect, useState, useCallback } from "react";
import { Card, Row, Col, Table, Button, Modal, Form, Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function ManageIslamicCurriculum() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [syllabi, setSyllabi]       = useState([]);
  const [yearList, setYearList]     = useState([]);
  const [yearId, setYearId]         = useState(localStorage.getItem("yearId") || "");
  const [assigned, setAssigned]     = useState([]);
  const [classes, setClasses]       = useState([]);
  const [programs, setPrograms]     = useState([]);

  const [showSylModal, setShowSylModal] = useState(false);
  const [newYear, setNewYear]           = useState("");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm]           = useState({ iss_id: "", ic_id: "", program_id: "" });

  const fetchSyllabi = useCallback(() => {
    axiosInstance.get("/api/islamic/getSyllabi").then(r => setSyllabi(r.data)).catch(() => {});
  }, []);

  const fetchAssigned = useCallback(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`/api/islamic/getAssignSyllabus/${yearId}`)
      .then(r => setAssigned(r.data))
      .catch(() => setAssigned([]))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  useEffect(() => {
    axiosInstance.get("/api/getStudyYear").then(r => {
      setYearList(r.data);
      if (!yearId && r.data.length > 0) {
        const id = String(r.data[0].year_id);
        localStorage.setItem("yearId", id);
        setYearId(id);
      }
    });
    axiosInstance.get("/api/islamic/getIslamicClasses").then(r => setClasses(r.data));
    axiosInstance.get("/api/islamic/getPrograms").then(r => setPrograms(r.data));
    fetchSyllabi();
  }, [fetchSyllabi]);

  useEffect(() => { fetchAssigned(); }, [fetchAssigned]);

  const handleAddSyllabus = async () => {
    if (!newYear) return;
    try {
      await axiosInstance.post("/api/islamic/addSyllabus", { iss_year: newYear });
      Swal.fire(t.manageCurriculum.successTitle, t.manageCurriculum.addedSyl, "success");
      setShowSylModal(false); setNewYear(""); fetchSyllabi();
    } catch (err) {
      Swal.fire(t.manageCurriculum.errorTitle, err.response?.data?.message || t.common.error, "error");
    }
  };

  const handleDeleteSyllabus = (s) => {
    Swal.fire({
      title: t.manageCurriculum.confirmDeleteSyl(s.iss_year),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t.manageCurriculum.confirmBtn,
      cancelButtonText: t.manageCurriculum.cancelBtn,
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.post("/api/islamic/deleteSyllabus", { iss_id: s.iss_id })
        .then(() => { Swal.fire(t.manageCurriculum.successTitle, t.manageCurriculum.deletedSyl, "success"); fetchSyllabi(); })
        .catch(err => Swal.fire(t.manageCurriculum.errorTitle, err.response?.data?.message || t.common.error, "error"));
    });
  };

  const handleAddAssign = async () => {
    const { iss_id, ic_id } = assignForm;
    if (!iss_id || !ic_id) { Swal.fire(t.manageCurriculum.incompleteTitle, t.manageCurriculum.incompleteAssignMsg, "warning"); return; }
    try {
      await axiosInstance.post("/api/islamic/addAssignSyllabus", { ...assignForm, year_id: yearId });
      Swal.fire(t.manageCurriculum.successTitle, t.manageCurriculum.assignedSuccess, "success");
      setShowAssignModal(false); fetchAssigned();
    } catch (err) {
      Swal.fire(t.manageCurriculum.errorTitle, err.response?.data?.message || t.common.error, "error");
    }
  };

  const handleDeleteAssign = (a) => {
    Swal.fire({
      title: t.manageCurriculum.confirmCancelAssign,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t.manageCurriculum.confirmBtn,
      cancelButtonText: t.manageCurriculum.cancelBtn,
    }).then(r => {
      if (!r.isConfirmed) return;
      axiosInstance.post("/api/islamic/deleteAssignSyllabus", { isa_id: a.isa_id })
        .then(() => { Swal.fire(t.manageCurriculum.successTitle, t.manageCurriculum.cancelledAssign, "success"); fetchAssigned(); })
        .catch(err => Swal.fire(t.manageCurriculum.errorTitle, err.response?.data?.message || t.common.error, "error"));
    });
  };

  return (
    <React.Fragment>
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item"><Link to="/dashboard/school-statistic">{t.manageCurriculum.breadHome}</Link></li>
            <li className="breadcrumb-item active">{t.manageCurriculum.breadCurrent}</li>
          </ol>
          <h4 className="main-title mb-0">{t.manageCurriculum.pageTitle}</h4>
        </div>
      </div>

      <Row className="g-3">
        <Col xl={4}>
          <Card className="card-one h-100">
            <Card.Header className="d-flex align-items-center">
              <Card.Title as="h6" className="mb-0">{t.manageCurriculum.syllabusTitle}</Card.Title>
              <Button size="sm" variant="primary" className="ms-auto" onClick={() => setShowSylModal(true)}>
                <i className="ri-add-line me-1"></i>{t.manageCurriculum.addBtn}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-secondary">
                  <tr><th>{t.manageCurriculum.colSylYear}</th><th></th></tr>
                </thead>
                <tbody>
                  {syllabi.length === 0 && <tr><td colSpan={2} className="text-center text-secondary py-3">{t.manageCurriculum.noData}</td></tr>}
                  {syllabi.map(s => (
                    <tr key={s.iss_id}>
                      <td className="fw-medium">{s.iss_year}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteSyllabus(s)}><i className="ri-delete-bin-line"></i></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8}>
          <Card className="card-one">
            <Card.Header className="d-flex align-items-center flex-wrap gap-2">
              <Card.Title as="h6" className="mb-0">{t.manageCurriculum.assignedTitle}</Card.Title>
              <div className="d-flex gap-2 ms-auto align-items-center">
                <Form.Select size="sm" value={yearId} onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }} style={{ width: 120 }}>
                  {yearList.map(y => <option key={y.year_id} value={y.year_id}>{t.common.studyYearShort(y.year_year)}</option>)}
                </Form.Select>
                <Button size="sm" variant="primary" onClick={() => { setAssignForm({ iss_id: syllabi[0]?.iss_id || "", ic_id: "", program_id: "" }); setShowAssignModal(true); }}>
                  <i className="ri-add-line me-1"></i>{t.manageCurriculum.assignBtn}
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-secondary">
                  <tr>
                    <th>#</th>
                    <th>{t.manageCurriculum.colSyllabus}</th>
                    <th>{t.manageCurriculum.colClass}</th>
                    <th>{t.manageCurriculum.colProgram}</th>
                    <th>{t.manageCurriculum.colSubjects(0).replace('0', '')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {assigned.length === 0 && <tr><td colSpan={6} className="text-center text-secondary py-3">{t.manageCurriculum.noData}</td></tr>}
                  {assigned.map((a, i) => (
                    <tr key={a.isa_id}>
                      <td className="text-secondary">{i + 1}</td>
                      <td>{a.iss_year}</td>
                      <td className="fw-medium">{a.ic_class}</td>
                      <td>{a.is_programCode || "-"}</td>
                      <td>
                        <Link to={`/islamic/manage-subjects/${a.isa_id}`} className="text-decoration-none">
                          <Badge bg="secondary" className="fw-normal">{t.manageCurriculum.colSubjects(a.subject_count)}</Badge>
                        </Link>
                      </td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAssign(a)}><i className="ri-delete-bin-line"></i></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showSylModal} onHide={() => setShowSylModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t.manageCurriculum.modalAddSylTitle}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Label className="fw-medium">{t.manageCurriculum.labelSylYear}</Form.Label>
          <Form.Control value={newYear} onChange={e => setNewYear(e.target.value)} placeholder="2567" maxLength={4} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSylModal(false)}>{t.common.cancel}</Button>
          <Button variant="primary" onClick={handleAddSyllabus}>{t.common.save}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>{t.manageCurriculum.modalAssignTitle}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={12}>
              <Form.Label className="fw-medium">{t.manageCurriculum.labelSyllabus}</Form.Label>
              <Form.Select value={assignForm.iss_id} onChange={e => setAssignForm(f => ({ ...f, iss_id: e.target.value }))}>
                <option value="">{t.manageCurriculum.sylPlaceholder}</option>
                {syllabi.map(s => <option key={s.iss_id} value={s.iss_id}>{s.iss_year}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageCurriculum.labelClass}</Form.Label>
              <Form.Select value={assignForm.ic_id} onChange={e => setAssignForm(f => ({ ...f, ic_id: e.target.value }))}>
                <option value="">{t.manageCurriculum.classPlaceholder}</option>
                {classes.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6}>
              <Form.Label className="fw-medium">{t.manageCurriculum.labelProgram}</Form.Label>
              <Form.Select value={assignForm.program_id} onChange={e => setAssignForm(f => ({ ...f, program_id: e.target.value }))}>
                <option value="">{t.manageCurriculum.noneOption}</option>
                {programs.map(p => <option key={p.is_programId} value={p.is_programId}>{p.is_programCode}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>{t.common.cancel}</Button>
          <Button variant="primary" onClick={handleAddAssign}>{t.common.save}</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
